import { decomposeDuration } from "./relativeTime.js";

/**
 * alertSpeech.js — Convierte una alerta en la frase que LOCUTA el navegador.
 *
 * Es un texto distinto del que va en la notificación push (ése lo arma
 * `backend/src/notifications/alertPhrase.js`), y la diferencia es deliberada:
 *
 *   pantalla → "XYZ01H a 95 km/h"
 *   voz      → "XYZ cero uno H a 95 kilómetros por hora"
 *
 * Un sintetizador leyendo "km/h" dice "ka eme barra hache", y leyendo "XYZ01H"
 * de corrido lo convierte en una palabra impronunciable. En pantalla, en
 * cambio, las unidades deletreadas ocupan media línea de más. Son dos medios
 * distintos; consolidarlos degradaría los dos.
 */

/**
 * Alfabeto fonético en español.
 *
 * NO es el alfabeto OTAN (Alfa, Bravo, Charlie…): son palabras españolas
 * comunes, que es lo que funciona cuando quien escucha no habla inglés. "B" y
 * "V" en español suenan casi igual y son EL error clásico al dictar una placa;
 * "Barco" y "Vela" no se confunden ni con ruido de fondo.
 */
const PHONETIC_LETTERS = {
  A: "Alfa",    B: "Barco",  C: "Casa",   D: "Dado",
  E: "Eco",     F: "Foco",   G: "Gato",   H: "Hora",
  I: "Isla",    J: "Jarra",  K: "Kilo",   L: "Luna",
  M: "Mano",    N: "Nube",   O: "Oso",    P: "Papel",
  Q: "Queso",   R: "Radio",  S: "Sapo",   T: "Tigre",
  U: "Uva",     V: "Vela",   W: "Wafle",  X: "Xilófono",
  Y: "Yate",    Z: "Zapato",
};

// En modo fonético los dígitos también van uno por uno. Leer "01" como número
// da "uno" — el cero se pierde, y una placa mal anotada es peor que no oírla.
const PHONETIC_DIGITS = {
  0: "cero", 1: "uno",   2: "dos",  3: "tres",  4: "cuatro",
  5: "cinco", 6: "seis", 7: "siete", 8: "ocho", 9: "nueve",
};

/** 10 a 29 en palabras: los irregulares del español, que no salen de una regla. */
const SPOKEN_10_29 = {
  10: "diez",        11: "once",         12: "doce",         13: "trece",
  14: "catorce",     15: "quince",       16: "dieciséis",    17: "diecisiete",
  18: "dieciocho",   19: "diecinueve",   20: "veinte",       21: "veintiuno",
  22: "veintidós",   23: "veintitrés",   24: "veinticuatro", 25: "veinticinco",
  26: "veintiséis",  27: "veintisiete",  28: "veintiocho",   29: "veintinueve",
};

/** Las decenas de 30 en adelante, que sí siguen la regla "treinta y uno". */
const SPOKEN_TENS = {
  3: "treinta", 4: "cuarenta", 5: "cincuenta",
  6: "sesenta", 7: "setenta",  8: "ochenta", 9: "noventa",
};

/**
 * Los dos dígitos de una placa, como los lee una persona: `"18"` → "dieciocho".
 *
 * La placa de acá es 3 letras + 2 dígitos + letra, y el par de dígitos es UN
 * número, no dos: quien la dicta por radio dice "dieciocho", y "uno ocho"
 * obliga a quien escucha a rearmarlo.
 *
 * Con cero adelante se mantienen los dos dígitos sueltos ("cero uno"): "01"
 * como número es "uno" y el cero desaparece de una placa que sí lo lleva.
 */
function spokenPair(pair) {
  if (pair[0] === "0") return `${PHONETIC_DIGITS[pair[0]]} ${PHONETIC_DIGITS[pair[1]]}`;

  const n = Number(pair);
  if (SPOKEN_10_29[n]) return SPOKEN_10_29[n];

  const unidad = Number(pair[1]);
  const decena = SPOKEN_TENS[Number(pair[0])];
  return unidad === 0 ? decena : `${decena} y ${PHONETIC_DIGITS[unidad]}`;
}

/**
 * Prepara una placa para el TTS.
 *
 * Modo normal — las letras van tal cual y los dígitos deletreados como palabras:
 * `"XYZ01H"` → `"XYZ cero uno H"`. Sin eso los motores en español silabean
 * "XYZ01H" como una palabra impronunciable, y con los dígitos en cifras leen
 * "01 H" como una hora.
 *
 * Modo fonético — deletrea carácter por carácter: `"XYZ01H"` → `"Xilófono Yate
 * Zapato cero uno Hora"`. Más largo, pero es la única forma de que la placa se
 * entienda a la primera con ruido, con el volumen bajo o por un parlante malo.
 *
 * @param {string} plate
 * @param {{phonetic?: boolean}} [options]
 */
export function speakablePlate(plate, { phonetic = false } = {}) {
  const raw = String(plate ?? "").trim();
  if (!raw) return "";

  if (phonetic) {
    return raw
      .split("")
      .map((ch) => {
        const upper = ch.toUpperCase();
        if (PHONETIC_LETTERS[upper]) return PHONETIC_LETTERS[upper];
        if (PHONETIC_DIGITS[ch]) return PHONETIC_DIGITS[ch];
        // Letra sin entrada en la tabla (Ñ, acentuada): se locuta tal cual en
        // vez de desaparecer — una placa incompleta no identifica nada. Todo
        // lo demás (guiones, espacios, puntos) se descarta: son separadores de
        // escritura, y el motor los leería como "guion".
        return /\p{L}/u.test(ch) ? upper : "";
      })
      .filter(Boolean)
      .join(" ");
  }

  return raw
    // Los separadores de escritura pasan a espacio ANTES de nada: las placas
    // llegan tanto "ZUY86H" como "ZUY-86H" según cómo se cargó el vehículo, y
    // un guion leído en voz alta es "guion".
    .replace(/[-_.\s]+/g, " ")
    // Los dígitos van en PALABRAS, nunca en cifras: "XYZ 01 H" lo leía el motor
    // como la hora "la una", porque `01 h` es la notación de 24 horas en
    // español. Una placa mal entendida no identifica ningún vehículo, que es lo
    // único que la locución tiene que lograr.
    //
    // El par de la placa de acá (3 letras + 2 dígitos + letra) se lee como UN
    // número —"dieciocho", no "uno ocho"—, que es como se dicta por radio. Un
    // grupo de otro largo sigue yendo dígito por dígito: en una placa de auto
    // (3 dígitos) "ciento veintitrés" ya no se retiene de oído.
    //
    // Como cada grupo queda rodeado de espacios, esto también separa el bloque
    // de letras del de números sin necesidad de regex de frontera.
    .replace(/\d+/g, (grupo) =>
      grupo.length === 2
        ? ` ${spokenPair(grupo)} `
        : ` ${grupo.split("").map((d) => PHONETIC_DIGITS[d]).join(" ")} `,
    )
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Etiqueta hablada del vehículo: la placa si existe, y si no el IMEI.
 *
 * Un IMEI son 15 dígitos seguidos: leído como número da "ochocientos sesenta y
 * cinco billones…", que no identifica nada. Se locutan solo los últimos 6, en
 * grupos de 3 (así el motor los lee como dos números cortos), que es lo que un
 * operador usa para reconocer un equipo sin placa. En modo fonético van dígito
 * por dígito, por el mismo motivo que la placa.
 */
export function speakableLabel({ plate, imei, phonetic = false }) {
  if (plate) return speakablePlate(plate, { phonetic });

  const digits = String(imei ?? "").replace(/\D/g, "");
  if (!digits) return "un equipo";

  const tail = digits.length <= 6 ? digits : digits.slice(-6);
  if (phonetic) {
    return `equipo ${tail.split("").map((d) => PHONETIC_DIGITS[d]).join(" ")}`;
  }
  if (digits.length <= 6) return `equipo ${tail}`;
  return `equipo ${tail.slice(0, 3)} ${tail.slice(3)}`;
}

/**
 * Nombre hablado del conductor asignado: PRIMER nombre + PRIMER apellido.
 *
 * "JUAN CARLOS PÉREZ GÓMEZ" → "JUAN PÉREZ". El nombre completo alarga la frase
 * sin identificar mejor a nadie: quien monitorea reconoce a su conductor por el
 * nombre con el que lo llama, y los cuatro tokens seguidos hacen que la alerta
 * —el motivo real del anuncio— llegue tarde.
 *
 * Por eso nombre y apellido llegan SEPARADOS desde el backend: partir el
 * `first_name || ' ' || last_name` ya concatenado es imposible sin adivinar
 * dónde termina un nombre compuesto.
 *
 * @param {{firstName?: string|null, lastName?: string|null}} [driver]
 * @returns {string} vacío si no hay conductor asignado
 */
export function speakableDriver({ firstName, lastName } = {}) {
  // Solo el primer token de cada campo, y sin caracteres que el TTS lea como
  // signos: un apellido cargado como "PEREZ-GOMEZ" se locutaría "guion".
  const primero = (value) =>
    String(value ?? "")
      .replace(/[-_.]+/g, " ")
      .trim()
      .split(/\s+/)[0] ?? "";

  return [primero(firstName), primero(lastName)].filter(Boolean).join(" ");
}

/**
 * Modos de identificación del vehículo en la locución.
 *
 * `plate` es el default histórico. `driver` y `both` existen porque quien
 * despacha razona en personas, no en placas: "Juan salió de la Oficina" es
 * accionable de inmediato, mientras que la placa exige un paso mental de
 * traducción. `both` es para flotas donde el mismo conductor rota de vehículo.
 */
export const IDENTIFY_MODES = Object.freeze(["plate", "driver", "both"]);

/**
 * El sujeto de la frase: qué se nombra antes de decir qué pasó.
 *
 * Si el modo pide conductor y el vehículo no tiene uno asignado, cae a la placa
 * en vez de anunciar un vehículo anónimo — una alerta que no dice QUIÉN o QUÉ
 * no sirve para nada, y sin asignación la placa es el único dato que hay.
 *
 * @param {object} args
 * @param {string|null} [args.plate]
 * @param {string} [args.imei]
 * @param {string|null} [args.driverFirstName]
 * @param {string|null} [args.driverLastName]
 * @param {boolean} [args.phonetic]  — deletrea la placa, nunca el nombre
 * @param {"plate"|"driver"|"both"} [args.identify]
 */
export function speakableSubject({
  plate,
  imei,
  driverFirstName,
  driverLastName,
  phonetic = false,
  identify = "plate",
} = {}) {
  const vehiculo = speakableLabel({ plate, imei, phonetic });
  const conductor = speakableDriver({ firstName: driverFirstName, lastName: driverLastName });

  if (!conductor || identify === "plate") return vehiculo;
  if (identify === "driver") return conductor;
  // "both": la placa primero y el conductor como aposición. El orden importa —
  // la placa es lo que el operador cruza con el mapa y la lista, así que es lo
  // que tiene que llegar mientras todavía está prestando atención.
  return `${vehiculo} con ${conductor}`;
}

/**
 * Duración en palabras completas para el TTS: "1 hora 30 minutos".
 *
 * NO usa `formatDurationMinutes()` de formatCop.js a propósito — aquél abrevia
 * a "1 h 30 min", que el sintetizador lee como "hache" y "eme i ene". La
 * descomposición sí sale de `decomposeDuration()`, que es la fuente única.
 */
export function spokenDuration(totalMinutes) {
  const n = Number(totalMinutes);
  if (!Number.isFinite(n) || n < 1) return "menos de un minuto";

  const d = decomposeDuration(Math.round(n) * 60);
  const units = [
    [d.years, "año", "años"],
    [d.months, "mes", "meses"],
    [d.days, "día", "días"],
    [d.hours, "hora", "horas"],
    [d.minutes, "minuto", "minutos"],
  ];

  // Dos unidades máximo: locutar "1 día 5 horas 12 minutos" es una frase que
  // ya nadie sigue, y el dato accionable está en la unidad más grande.
  return units
    .filter(([value]) => value > 0)
    .slice(0, 2)
    .map(([value, one, many]) => `${value} ${value === 1 ? one : many}`)
    .join(" ");
}

// Mismas reglas de lectura del payload que los otros dos módulos de frase
// (ver backend/src/notifications/alertPhrase.js): minutos no finitos caen a la
// variante sin dato, el nombre de geocerca se normaliza, y la velocidad se lee
// también de `speed` — que es la clave que el emisor realmente escribe.
function mins(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function zoneName(payload) {
  const name = String(payload?.geofence_name ?? "").trim();
  return name || null;
}

function speedKmh(payload) {
  const n = Number(payload?.speed_kmh ?? payload?.speed);
  // > 0: el bloque GPS del paquete de alarma puede llegar sin fix y con speed
  // en 0. "ZUY86H a 0 km/h" como alerta de exceso se lee como un bug de la
  // app; mejor la frase genérica, que al menos no miente.
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

const PHRASES = {
  GEOFENCE_ENTER: (who, p) => {
    const zona = zoneName(p);
    return zona ? `${who} entró a ${zona}` : `${who} entró a una geocerca`;
  },

  GEOFENCE_EXIT: (who, p) => {
    const zona = zoneName(p);
    return zona ? `${who} salió de ${zona}` : `${who} salió de una geocerca`;
  },

  GEOFENCE_DWELL: (who, p) => {
    const zona = zoneName(p);
    const donde = zona ? ` en ${zona}` : " dentro de la geocerca";
    const minutos = mins(p?.dwell_minutes);
    return minutos != null
      ? `${who} lleva ${spokenDuration(minutos)}${donde}`
      : `${who} sigue${donde}`;
  },

  // "estacionada" por decisión del producto (así se locutaba antes de #613),
  // aunque con el conductor como sujeto el adjetivo no concuerde.
  PARKED: (who, p) => {
    const minutos = mins(p?.threshold_minutes);
    return minutos != null
      ? `${who} lleva ${spokenDuration(minutos)} estacionada`
      : `${who} lleva mucho tiempo estacionada`;
  },

  OVERSPEED: (who, p) => {
    const kmh = speedKmh(p);
    return kmh != null
      ? `${who} a ${kmh} kilómetros por hora`
      : `${who} con exceso de velocidad`;
  },

  // Ver la nota en el módulo del backend: hoy ningún emisor escribe
  // `battery_percent`, así que esta rama no se ejecuta.
  LOW_BATTERY: (who, p) =>
    p?.battery_percent != null
      ? `${who} con batería en ${p.battery_percent} por ciento`
      : `${who} con batería baja`,

  // Con el motor encendido y en marcha el corte es cableado, no un robo: se
  // locuta la diferencia porque el operador decide distinto al oírla.
  BATTERY_CUT: (who, p) =>
    p?.engine_on === true && p?.moving === true
      ? `${who} desconectó la batería con el motor encendido`
      : `${who} desconectó la batería`,
  // El robo va primero y la razón después: a las 3 a.m. lo que importa es
  // "posible robo" y la placa; el resto es contexto.
  THEFT_SUSPECTED: (who, p) => {
    const kmh = speedKmh(p);
    const razon = kmh != null
      ? `se mueve con la batería desconectada a ${kmh} kilómetros por hora`
      : "se mueve con la batería desconectada";
    return `${who} en posible robo, ${razon}${p?.engine_off === true ? ", con el motor apagado" : ""}`;
  },
  POWER_RESTORED: (who, p) => {
    const minutos = mins(p?.minutes_without_power);
    return minutos != null
      ? `${who} conectó la batería después de ${spokenDuration(minutos)}`
      : `${who} conectó la batería`;
  },
  // El equipo avisando que se apaga, NO el corte de batería (ése es
  // BATTERY_CUT). Ver la nota en backend/src/notifications/alertPhrase.js.
  POWER_OFF: (who) => `${who} se apagó`,
  SOS: (who) => `${who} activó el botón de pánico`,
  ENGINE_ON: (who) => `${who} encendió el motor`,
  ENGINE_OFF: (who) => `${who} apagó el motor`,
  VIBRATION: (who) => `${who} detectó vibración`,

  GPS_STALE: (who, p) => {
    const minutos = mins(p?.stale_minutes);
    return minutos != null
      ? `${who} sin posición GPS hace ${spokenDuration(minutos)}`
      : `${who} sin posición GPS`;
  },

  DEVICE_OFFLINE: (who, p) => {
    const minutos = mins(p?.offline_minutes);
    return minutos != null
      ? `${who} se desconectó hace ${spokenDuration(minutos)}`
      : `${who} se desconectó`;
  },
  TAMPERING: (who) => `${who} con el equipo manipulado`,
  REMOVING: (who) => `${who} con el equipo removido`,
  ACCIDENT: (who) => `${who} tuvo un posible accidente`,
  FALL_DOWN: (who) => `${who} sufrió una caída`,
  JAMMING: (who) => `${who} con la señal inhibida`,
  DOOR: (who) => `${who} abrió una puerta`,
};

/**
 * Arma la frase a locutar para una alerta recibida por WebSocket.
 *
 * @param {object} alert
 * @param {string} alert.type          — alert_type del backend
 * @param {string|null} [alert.plate]
 * @param {string} [alert.imei]
 * @param {string|null} [alert.driverFirstName] — conductor asignado
 * @param {string|null} [alert.driverLastName]
 * @param {object} [alert.payload]
 * @param {boolean} [alert.phonetic]   — deletrear la placa (Xilófono Yate…)
 * @param {"plate"|"driver"|"both"} [alert.identify] — a quién se nombra
 * @returns {string} frase lista para speechSynthesis (nunca vacía)
 */
export function alertSpeech({
  type,
  plate,
  imei,
  driverFirstName,
  driverLastName,
  payload,
  phonetic = false,
  identify = "plate",
} = {}) {
  const who = speakableSubject({
    plate,
    imei,
    driverFirstName,
    driverLastName,
    phonetic,
    identify,
  });
  const template = PHRASES[type];
  if (template) return template(who, payload ?? {});

  // Tipo nuevo que el backend ya emite y el frontend todavía no conoce: se
  // anuncia el vehículo igual. Saber QUÉ vehículo con una alerta genérica es
  // infinitamente más útil que el silencio.
  return `${who} tiene una alerta`;
}
